import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  createMilestone,
  updateMilestone,
  deleteMilestone,
  reorderMilestones,
} from '@/api/calendars'
import type {
  CalendarMilestoneCreate,
  CalendarMilestoneUpdate,
  ReorderRequest,
} from '@/types'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'
import { invalidateCalendarQueries } from '@/hooks/useCalendarQueryHelpers'

export function useCreateMilestone(calendarId: string, eventId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CalendarMilestoneCreate) =>
      createMilestone(calendarId, eventId, payload),
    onSuccess: () => {
      invalidateCalendarQueries(queryClient, calendarId)
      toast.success('Milestone created successfully')
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to create milestone'))
    },
  })
}

export function useUpdateMilestone(calendarId: string, eventId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      msId,
      payload,
    }: {
      msId: string
      payload: CalendarMilestoneUpdate
    }) => updateMilestone(calendarId, eventId, msId, payload),
    onSuccess: () => {
      invalidateCalendarQueries(queryClient, calendarId)
      toast.success('Milestone updated successfully')
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update milestone'))
    },
  })
}

export function useDeleteMilestone(calendarId: string, eventId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (msId: string) => deleteMilestone(calendarId, eventId, msId),
    onSuccess: () => {
      invalidateCalendarQueries(queryClient, calendarId)
      toast.success('Milestone deleted successfully')
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to delete milestone'))
    },
  })
}

export function useReorderMilestones(calendarId: string, eventId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ReorderRequest) =>
      reorderMilestones(calendarId, eventId, payload),
    onSuccess: () => {
      invalidateCalendarQueries(queryClient, calendarId)
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to reorder milestones'))
    },
  })
}
